import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import API_BASE_URL from '../../config/api';
import { useAuth } from '../../context/AuthContext.jsx';
import styles from './BankDetailsForm.module.scss';

const TokenEdit = () => {
  const { makeAuthenticatedRequest } = useAuth();
  const [searchParams] = useSearchParams();
  const urlToken = searchParams.get('token');
  
  const [token, setToken] = useState(urlToken || '');
  const [formData, setFormData] = useState({
    office_name: '',
    accountant_name: '',
    official_email: '',
    bank_accounts: []
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [showForm, setShowForm] = useState(false);

  useEffect(() => {
    if (urlToken) {
      setToken(urlToken);
      handleTokenSubmit();
    }
  }, [urlToken]);

  const handleTokenSubmit = async () => {
    if (!token.trim()) {
      setError('कृपया टोकन भर्नुहोस्');
      return;
    }

    setIsLoadingData(true);
    setError('');
    setSuccess('');

    try {
      // First, check if the token is valid and active for editing
      const validityResponse = await makeAuthenticatedRequest(`${API_BASE_URL}/api/bank-details/check_token_validity/?reference_code=${token.trim()}`);
      const validityData = await validityResponse.json();
      
      if (!validityResponse.ok) {
        if (validityData.message === 'Token expired') {
          setError('यो टोकन म्याद सकिएको छ र अब सम्पादन गर्न मिल्दैन। कृपया प्रशासकलाई सम्पर्क गर्नुहोस्।');
        } else if (validityData.message === 'Token not activated') {
          setError('यो टोकन अहिले सम्पादनको लागि सक्रिय छैन। कृपया प्रशासकलाई सम्पर्क गर्नुहोस्।');
        } else {
          setError(validityData.error || 'टोकन जाँच गर्न असफल');
        }
        return;
      }
      
      if (!validityData.valid) {
        setError(validityData.error || 'टोकन मान्य छैन');
        return;
      }

      // Token is valid, now fetch the bank details
      const response = await makeAuthenticatedRequest(`${API_BASE_URL}/api/bank-details/?reference_code=${token.trim()}`);
      if (!response.ok) {
        throw new Error('Failed to fetch bank details');
      }
      
      const data = await response.json();
      
      if (data.results && data.results.length > 0) {
        // Group by office and account head
        const groupedData = {};
        data.results.forEach(item => {
          const key = `${item.office_name}_${item.account_head_name}_${item.official_email}`;
          if (!groupedData[key]) {
            groupedData[key] = {
              office_name: item.office_name,
              accountant_name: item.account_head_name,
              official_email: item.official_email,
              bank_accounts: []
            };
          }
          groupedData[key].bank_accounts.push({
            id: item.id,
            bank_name: item.bank_name,
            account_type: item.account_type,
            account_number: item.account_number
          });
        });

        // Take the first group (assuming single group for token-based editing)
        const firstGroup = Object.values(groupedData)[0];
        if (firstGroup) {
          // Add empty rows to match the entry form structure (minimum 6 rows)
          const existingAccounts = firstGroup.bank_accounts;
          const emptyRowsNeeded = Math.max(6 - existingAccounts.length, 2); // At least 2 empty rows
          const emptyRows = Array(emptyRowsNeeded).fill({ bank_name: '', account_type: '', account_number: '' });
          
          setFormData({
            office_name: firstGroup.office_name || '',
            accountant_name: firstGroup.accountant_name || '',
            official_email: firstGroup.official_email || '',
            bank_accounts: [...existingAccounts, ...emptyRows]
          });
          setShowForm(true);
          setSuccess('टोकन मान्य छ र सम्पादन गर्न सकिन्छ।');
        }
      } else {
        setError('यो टोकनको लागि कुनै बैंक विवरण फेला परेनन्');
      }
    } catch (err) {
      setError(err.message || 'बैंक विवरण लोड गर्न असफल');
    } finally {
      setIsLoadingData(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleBankAccountChange = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      bank_accounts: prev.bank_accounts.map((account, i) => 
        i === index ? { ...account, [field]: value } : account
      )
    }));
  };

  const addNewBankAccount = () => {
    setFormData(prev => ({
      ...prev,
      bank_accounts: [...prev.bank_accounts, { bank_name: '', account_type: '', account_number: '' }]
    }));
  };

  const removeBankAccount = (index) => {
    if (formData.bank_accounts.length > 1) {
      setFormData(prev => ({
        ...prev,
        bank_accounts: prev.bank_accounts.filter((_, i) => i !== index)
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      // Filter out completely empty bank accounts
      const validBankAccounts = formData.bank_accounts.filter(
        account => account.bank_name.trim() || account.account_type.trim() || account.account_number.trim()
      );

      if (validBankAccounts.length === 0) {
        setError('कृपया कम्तिमा एउटा बैंक खाताको विवरण भर्नुहोस्।');
        setLoading(false);
        return;
      }

      // Prepare update data
      const updateData = {
        office_name: formData.office_name,
        account_head_name: formData.accountant_name,
        official_email: formData.official_email,
        bank_accounts: validBankAccounts
      };

      // Make bulk update request
      const response = await makeAuthenticatedRequest(`${API_BASE_URL}/api/bank-details/bulk_update_group/`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updateData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update bank details');
      }

      const result = await response.json();
      setSuccess('बैंक विवरण सफलतापूर्वक अपडेट गरियो!');

    } catch (err) {
      setError(err.message || 'बैंक विवरण अपडेट गर्न असफल');
    } finally {
      setLoading(false);
    }
  };

  // Show token search form if no form is displayed yet
  if (!showForm) {
    return (
      <div className={styles.modalOverlay} style={{ position: 'static', backgroundColor: 'transparent' }}>
        <div className={styles.modal} style={{ maxWidth: '600px', margin: '50px auto' }}>
          <div className={styles.modalHeader}>
            <h2>टोकन प्रविष्टि</h2>
          </div>
          
          <div style={{ padding: '20px' }}>
            <p style={{ marginBottom: '20px', textAlign: 'center' }}>
              कृपया आफ्नो टोकन भर्नुहोस् र बैंक विवरण सम्पादन गर्नुहोस्
            </p>
            
            <div style={{ marginBottom: '20px' }}>
              <input
                type="text"
                value={token}
                onChange={(e) => setToken(e.target.value)}
                placeholder="टोकन भर्नुहोस्"
                style={{
                  width: '100%',
                  padding: '12px',
                  fontSize: '16px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  marginBottom: '10px'
                }}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    handleTokenSubmit();
                  }
                }}
              />
            </div>

            {error && <div className={styles.error}>{error}</div>}

            <div style={{ textAlign: 'center' }}>
              <button
                onClick={handleTokenSubmit}
                disabled={isLoadingData}
                style={{
                  padding: '12px 24px',
                  fontSize: '16px',
                  backgroundColor: '#007bff',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: isLoadingData ? 'not-allowed' : 'pointer',
                  opacity: isLoadingData ? 0.6 : 1
                }}
              >
                {isLoadingData ? 'लोड गर्दै...' : 'फारम खोल्नुहोस्'}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.modalOverlay} style={{ position: 'static', backgroundColor: 'transparent' }}>
      <div className={styles.modal} style={{ maxWidth: '1200px', margin: '20px auto' }}>
        <div className={styles.modalHeader}>
          <h2>बैंक खाता विवरण फारम - सम्पादन मोड</h2>
          <div style={{ fontSize: '14px', color: '#666' }}>
            टोकन: {token}
          </div>
        </div>

        <form onSubmit={handleSubmit} className={styles.form}>
          <div className={styles.tableContainer}>
            <table className={styles.formTable}>
              <tbody>
                <tr>
                  <th colSpan="4" className={styles.centerHeader}>
                    मध्यपश्चिम विश्वविद्यालय
                  </th>
                </tr>
                <tr>
                  <td>कार्यालयको नाम.-</td>
                  <td colSpan="3">
                    <input
                      type="text"
                      value={formData.office_name}
                      onChange={(e) => handleInputChange('office_name', e.target.value)}
                      placeholder="कार्यालयको नाम भर्नुहोस्"
                      required
                    />
                  </td>
                </tr>
                <tr>
                  <td>लेखा प्रमुखको नाम</td>
                  <td colSpan="3">
                    <input
                      type="text"
                      value={formData.accountant_name}
                      onChange={(e) => handleInputChange('accountant_name', e.target.value)}
                      placeholder="लेखा प्रमुखको नाम भर्नुहोस्"
                      required
                    />
                  </td>
                </tr>
                <tr>
                  <td>लेखा प्रमुखको <b>official Mail ID</b></td>
                  <td colSpan="3">
                    <input
                      type="email"
                      value={formData.official_email}
                      onChange={(e) => handleInputChange('official_email', e.target.value)}
                      placeholder="official email भर्नुहोस्"
                      required
                    />
                  </td>
                </tr>
                <tr>
                  <th colSpan="4" className={styles.centerHeader}>
                    कार्यालयको नाममा सञ्चालित सम्पूर्ण बैक खाताको विवरण
                  </th>
                </tr>
                <tr>
                  <th>क्र.सं.</th>
                  <th>बैंक नाम</th>
                  <th>खाताको प्रकार</th>
                  <th>खाता नं.</th>
                </tr>
                {formData.bank_accounts.map((account, index) => (
                  <tr key={index}>
                    <td>{index + 1}</td>
                    <td>
                      <input
                        type="text"
                        value={account.bank_name}
                        onChange={(e) => handleBankAccountChange(index, 'bank_name', e.target.value)}
                        placeholder="बैंकको नाम"
                      />
                    </td>
                    <td>
                      <input
                        type="text"
                        value={account.account_type}
                        onChange={(e) => handleBankAccountChange(index, 'account_type', e.target.value)}
                        placeholder="खाताको प्रकार"
                      />
                    </td>
                    <td>
                      <input
                        type="text"
                        value={account.account_number}
                        onChange={(e) => handleBankAccountChange(index, 'account_number', e.target.value)}
                        placeholder="खाता नंबर"
                      />
                    </td>
                    {formData.bank_accounts.length > 1 && (
                      <td>
                        <button
                          type="button"
                          onClick={() => removeBankAccount(index)}
                          className={styles.removeButton}
                          title="यो पङ्क्ति हटाउनुहोस्"
                        >
                          ×
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
                <tr>
                  <td colSpan="4" className={styles.addRowCell}>
                    <button
                      type="button"
                      onClick={addNewBankAccount}
                      className={styles.addRowButton}
                    >
                      + नयाँ पङ्क्ति थप्नुहोस्
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className={styles.note}>
            <p><b>नोट:-</b> सम्पूर्ण नमस्कार, माथि उल्लेखित विवरण अनिवार्यरुपमा भर्नुहोस् जानकारी गराइन्छ ।</p>
          </div>

          {error && <div className={styles.error}>{error}</div>}
          {success && <div className={styles.success}>{success}</div>}

          <div className={styles.formActions}>
            <button
              type="submit"
              className={styles.submitButton}
              disabled={loading}
            >
              {loading ? 'अपडेट गर्दै...' : 'अपडेट गर्नुहोस्'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TokenEdit;
